import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { extractLocation } from "@/lib/utils";
import { handlePlainTextCopy } from "@/lib/plainTextCopy";
import { useLocation } from "wouter";
import { useTickets, useCreateTicket, useUpdateTicket, useDeleteTicket, useArchiveTicket, useUnarchiveTicket, useTicketNotes, useCreateTicketNote, useTicketMissions, useCreateTicketMission, useTicketMessages, useCreateTicketMessage, useUpdateTicketMessage, useTicketPieces, useCreateTicketPiece, useUpdateTicketPiece, useDeleteTicketPiece, useTicketMedia } from "@/hooks/use-tickets";
import { TicketMediaDropzone, TicketMediaTab, uploadTicketMedia } from "@/components/ticket-media-uploader";
import { useProfiles } from "@/hooks/use-profiles";
import { useClients } from "@/hooks/use-clients";
import { useRobots, useResellers, useProducts } from "@/hooks/use-inventory";
import { useMissions, useCreateMission } from "@/hooks/use-missions";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MentionTextarea } from "@/components/mention-textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DateRangeCalendar } from "@/components/ui/calendar";
import { StatusBadge } from "@/components/status-badge";
import { SearchSelect } from "@/components/search-select";
import { Plus, Search, Trash2, TicketCheck, Send, ExternalLink, Truck, MessageSquare, Archive, ArchiveRestore, Sparkles, Copy, RefreshCw, Loader2, Link2, LayoutGrid, List, User, Bot, Calendar, ChevronDown, MessageCircle, X } from "lucide-react";
import { endOfDay, format, isValid, parseISO, startOfDay, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { authFetch } from "@/lib/authFetch";
import { queryClient } from "@/lib/queryClient";
import { resolveMissionTitle, resolveMissionTierName } from "@/lib/mission-display";
import type { Ticket, InsertTicket, Profile, Client, Robot, Mission, Reseller, TicketNote, TicketMission, Product, TicketMessage, TicketPiece } from "@shared/schema";
import { profileIdSchema, type ProfileId } from "@shared/identity";
import { canDo } from "@shared/roles";
import { MISSION_TYPES } from "@shared/missionTypeConfig";
import { TicketClientEmail, type TicketClientEmailOpenRequest } from "@/components/ticket-client-email";
import { TicketDiscussionEntry } from "@/components/ticket-discussion-entry";
import { resolveEmailModelLabel } from "@/lib/email-client-helpers";
import { countTicketOrigins, matchesTicketOrigin, type TicketOrigin } from "@/lib/ticket-origin";

type PuduRobotItem = {
  id: string;
  robotSn: string;
  productName: string | null;
  robotType: string | null;
  productImageUrl: string | null;
  resellerName: string | null;
  shopId: string | null;
};

const SOURCE_LABELS: Record<string, string> = {
  formulaire: "Formulaire",
  email: "Email",
  telephone: "Téléphone",
  chat: "Chat",
  autre: "Autre",
  courrier: "Courrier",
  portail_partenaire: "Portail",
};

const PRIORITY_LABELS: Record<string, string> = {
  faible: "Faible",
  moyen: "Moyen",
  eleve: "Élevé",
  urgent: "Urgent",
};

const STATUS_LABELS: Record<string, string> = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  en_attente_piece: "En attente pièce",
  resolu: "Résolu",
  ferme: "Fermé",
};

const MISSION_STATUS_LABELS: Record<string, string> = {
  option: "Option",
  confirme: "Confirmé",
  annule: "Annulé",
  realisee: "Réalisée",
};

type Periode = "all" | "7j" | "30j" | "custom";

function parseReceivedAt(value: string | Date | null | undefined): Date {
  return value instanceof Date ? value : parseISO(value ?? "");
}

function isInPeriode(
  receivedAt: string | Date | null | undefined,
  periode: Periode,
  plage: DateRange | undefined,
  now: Date,
): boolean {
  if (periode === "all" || (periode === "custom" && (!plage?.from || !plage?.to))) return true;
  const date = parseReceivedAt(receivedAt);
  if (!isValid(date)) return true;
  if (periode === "custom") {
    return date >= startOfDay(plage!.from!) && date <= endOfDay(plage!.to!);
  }
  return date >= subDays(now, periode === "7j" ? 7 : 30) && date <= now;
}

const parseProfileId = (value: string | null): ProfileId | null => {
  const parsed = value ? profileIdSchema.safeParse(value) : null;
  return parsed?.success ? parsed.data : null;
};

export default function TicketListPage() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const canDelete = profile?.accessRole === 'admin' || profile?.accessRole === 'super_admin';
  const { data: ticketsRaw = [], isLoading } = useTickets();
  const { data: profilesData = [] } = useProfiles();
  const { data: clientsData = [] } = useClients();
  const { data: robotsData = [] } = useRobots();
  const { data: resellersData = [] } = useResellers();
  const { data: productsData = [] } = useProducts();
  const { data: puduRobotsData = [] } = useQuery<PuduRobotItem[]>({
    queryKey: ["/api/pudu-robots"],
    queryFn: async () => {
      const res = await authFetch("/api/pudu-robots");
      if (!res.ok) throw new Error("Erreur chargement robots Pudu");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const tickets: Ticket[] = ticketsRaw;
  const profiles_list: Profile[] = profilesData;
  const clients_list: Client[] = clientsData;
  const robots_list: Robot[] = robotsData;
  const resellers_list: Reseller[] = resellersData;
  const products_list: Product[] = productsData;
  const technicians = profiles_list.filter((p) => (p as any).intervenantTerrain === true && p.isActive !== false);

  const createTicket = useCreateTicket();
  const [pendingMediaFiles, setPendingMediaFiles] = useState<File[]>([]);
  const submitLockRef = useRef(false);
  const updateTicket = useUpdateTicket();
  const deleteTicket = useDeleteTicket();
  const archiveTicket = useArchiveTicket();
  const unarchiveTicket = useUnarchiveTicket();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterTechnician, setFilterTechnician] = useState<string>("all");
  const [filterClient, setFilterClient] = useState<string>("all");
  const [filterOrigin, setFilterOrigin] = useState<TicketOrigin>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [periode, setPeriode] = useState<Periode>("all");
  const [plage, setPlage] = useState<DateRange>();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    try { return (localStorage.getItem("ticketsViewMode") as 'cards' | 'list') || 'cards'; }
    catch { return 'cards'; }
  });
  const switchView = (mode: 'cards' | 'list') => {
    setViewMode(mode);
    try { localStorage.setItem("ticketsViewMode", mode); } catch {}
  };

  const [form, setForm] = useState<Partial<InsertTicket> & Record<string, any>>({
    source: "formulaire",
    priority: "moyen",
    status: "nouveau",
    problemDescription: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    ancienNumero: "",
    creeParId: null,
    creeParType: null,
    deplacementTechnicien: false,
    dateResolution: null,
  });

  const otherFiltered = useMemo(() => {
    const now = new Date();
    return tickets.filter((t: any) => {
      if (periode !== "all" && (periode !== "custom" || (plage?.from && plage?.to)) && !isValid(parseReceivedAt(t.receivedAt))) {
        console.warn(`[tickets] received_at invalide pour le ticket ${t.id}`);
      }
      if (!isInPeriode(t.receivedAt, periode, plage, now)) return false;
      if (!showArchived && t.archive) return false;
      if (showArchived && !t.archive) return false;
      if (filterStatus.length > 0 && !filterStatus.includes(t.status)) return false;
      if (filterPriority !== "all" && t.priority !== filterPriority) return false;
      if (filterTechnician !== "all" && t.assignedTo !== filterTechnician) return false;
      if (filterClient !== "all" && t.clientId !== filterClient) return false;
      if (search) {
        const s = search.toLowerCase();
        const clientRaw = clients_list.find((c) => c.id === t.clientId);
        const clientName = (clientRaw as any)?.nomCommercial || clientRaw?.companyName || "";
        const resellerRaw = resellers_list.find((r) => r.id === t.resellerId);
        const resellerName = (resellerRaw as any)?.nomCommercial || resellerRaw?.companyName || "";
        return (
          t.ticketNumber.toLowerCase().includes(s) ||
          (t.contactName || "").toLowerCase().includes(s) ||
          clientName.toLowerCase().includes(s) ||
          (t.clientFinalNom || "").toLowerCase().includes(s) ||
          resellerName.toLowerCase().includes(s) ||
          (t.problemDescription || "").toLowerCase().includes(s) ||
          ((t.ancienNumero || "") + "").toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [tickets, filterStatus, filterPriority, filterTechnician, filterClient, search, clients_list, resellers_list, showArchived, periode, plage]);

  const originCounts = useMemo(() => countTicketOrigins(otherFiltered), [otherFiltered]);
  const filtered = useMemo(
    () => otherFiltered.filter(ticket => matchesTicketOrigin(ticket, filterOrigin)),
    [otherFiltered, filterOrigin],
  );
  const periodActive = periode === "7j" || periode === "30j" || (periode === "custom" && !!plage?.from && !!plage?.to);
  const periodEmptyLabel = filterOrigin === "client"
    ? "Aucun ticket client pour ces filtres"
    : filterOrigin === "revendeur"
      ? "Aucun ticket revendeur pour ces filtres"
      : filterOrigin === "unassigned"
        ? "Aucun ticket à rattacher pour ces filtres"
        : periodActive ? "Aucun ticket sur cette période" : "Aucun ticket trouvé";

  const handleArchive = async (id: string) => {
    await archiveTicket.mutateAsync(id);
    toast({ title: "Ticket archivé" });
    setArchiveConfirmId(null);
  };

  const handleUnarchive = async (id: string) => {
    await unarchiveTicket.mutateAsync(id);
    toast({ title: "Ticket désarchivé" });
  };

  // ── Deep-link : ?open=IMP-XXXX ou ?openId=<uuid> → ouvre le détail ────────
  const deepLinkProcessed = useRef(false);
  useEffect(() => {
    if (deepLinkProcessed.current || isLoading) return;
    const params = new URLSearchParams(window.location.search);
    const openParam = params.get("open");
    const openIdParam = params.get("openId");
    if (!openParam && !openIdParam) return;
    deepLinkProcessed.current = true;
    window.history.replaceState({}, "", "/tickets");
    let found: Ticket | undefined;
    if (openParam) {
      found = tickets.find((t: any) => t.ticketNumber === openParam) as Ticket | undefined;
    } else if (openIdParam) {
      found = tickets.find((t: any) => t.id === openIdParam) as Ticket | undefined;
    }
    if (found) {
      openDetail(found);
    } else {
      toast({ title: "Ticket introuvable", description: `Aucun ticket trouvé`, variant: "destructive" });
    }
  }, [isLoading, tickets]);
  // ───────────────────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingTicket(null);
    setForm({
      source: "formulaire",
      priority: "moyen",
      status: "nouveau",
      problemDescription: "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
      createdBy: profile?.id || null,
      ancienNumero: "",
      creeParId: null,
      creeParType: null,
      deplacementTechnicien: false,
      dateResolution: null,
    });
    setDialogOpen(true);
  };

  const openEdit = (ticket: Ticket) => {
    setEditingTicket(ticket);
    setForm({ ...ticket });
    setDialogOpen(true);
  };

  const openDetail = (ticket: Ticket) => {
    setSelectedTicketId(ticket.id);
    setEditingTicket(ticket);
    setDetailOpen(true);
  };

  const handleSubmit = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      const submitData = { ...form };
      submitData.titre = typeof submitData.titre === "string" ? submitData.titre.trim() || null : null;
      if (!editingTicket) {
        delete submitData.objet;
        if (!submitData.titre) {
          toast({
            title: "Objet non renseigné",
            description: "Le ticket sera créé avec sa référence automatique, sans objet métier.",
          });
        }
      }
      if ((submitData.status === "resolu" || submitData.status === "ferme") && !submitData.dateResolution) {
        (submitData as any).dateResolution = new Date().toISOString();
      }
      if (editingTicket) {
        await updateTicket.mutateAsync({ id: editingTicket.id, ...submitData });
        toast({ title: "Ticket mis à jour" });
        if (submitData.status === "ferme" && editingTicket.status !== "ferme" && !editingTicket.archive) {
          setArchiveConfirmId(editingTicket.id);
        }
      } else {
        const newTicket = await createTicket.mutateAsync({
          ...submitData,
          createdBy: profile?.id || null,
        } as InsertTicket);
        // Upload des médias en attente (un par un, les autres continuent même si l'un échoue)
        if (pendingMediaFiles.length > 0) {
          const filesToUpload = [...pendingMediaFiles];
          setPendingMediaFiles([]);
          for (const file of filesToUpload) {
            try {
              await uploadTicketMedia(newTicket.id, file);
            } catch (e: any) {
              toast({ title: `Erreur fichier "${file.name}"`, description: e.message, variant: "destructive" });
            }
          }
          await queryClient.invalidateQueries({ queryKey: ["/api/tickets/media", newTicket.id] });
        }
        toast({ title: "Ticket créé" });
      }
      setDialogOpen(false);
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      submitLockRef.current = false;
    }
  };

  const getClientOrResellerName = (ticket: Ticket) => {
    if (ticket.clientId) {
      const c = clients_list.find((c) => c.id === ticket.clientId);
      return (c as any)?.nomCommercial || c?.companyName || "-";
    }
    if (ticket.resellerId) {
      const r = resellers_list.find((r) => r.id === ticket.resellerId);
      return (r as any)?.nomCommercial || r?.companyName || "-";
    }
    return "-";
  };

  const getProfileName = (id: string | null) => {
    if (!id) return "-";
    const p = profiles_list.find((p) => p.id === id);
    return p ? `${p.firstName} ${p.lastName}` : "-";
  };

  const getPuduRobotName = (puduId: string | null, fallbackRobotId: string | null) => {
    if (puduId) {
      const pr = puduRobotsData.find((r) => r.id === puduId);
      if (pr) {
        const parts = [pr.productName, pr.resellerName].filter(Boolean).join(" · ");
        return `${pr.robotSn}${parts ? ` — ${parts}` : ""}`;
      }
    }
    if (fallbackRobotId) {
      const r = robots_list.find((r) => r.id === fallbackRobotId);
      return r ? `${r.modelName}${r.nickname ? ` (${r.nickname})` : ""}` : "-";
    }
    return "-";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <TicketCheck className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Tickets</h1>
          {!isLoading && (
            <span
              className="text-sm text-muted-foreground font-normal"
              data-testid="text-ticket-count"
            >
              {filtered.length === 1 ? "1 ticket" : `${filtered.length} tickets`}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex w-full min-w-0 items-center rounded-lg border bg-card p-0.5 gap-0.5 sm:w-auto" role="group" aria-label="Période des tickets" data-testid="filter-ticket-period">
            {([
              ["all", "Tout"],
              ["7j", "7 j"],
              ["30j", "30 j"],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={periode === value}
                data-testid={`filter-period-${value}`}
                onClick={() => { setPeriode(value); setCalendarOpen(false); }}
                className={`min-w-0 flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:flex-none sm:px-3 sm:text-sm ${periode === value ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                {label}
              </button>
            ))}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-pressed={periode === "custom"}
                  aria-label="Choisir une période personnalisée"
                  data-testid="filter-period-custom"
                  onClick={() => { if (periode !== "custom") { setPeriode("custom"); setPlage(undefined); } }}
                  className={`min-w-0 flex-[2] rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:flex-none sm:px-3 sm:text-sm ${periode === "custom" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <span className="block truncate">
                    {periode === "custom" && plage?.from && plage?.to
                      ? `${format(plage.from, "dd/MM")} → ${format(plage.to, "dd/MM")}`
                      : "Personnalisé"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto max-w-[calc(100vw-1rem)] p-2">
                <DateRangeCalendar
                  mode="range"
                  locale={fr}
                  selected={plage}
                  onSelect={(range) => {
                    setPlage(range);
                    if (range?.from && range?.to) setCalendarOpen(false);
                  }}
                  initialFocus
                />
                {plage?.from && !plage.to && (
                  <Button size="sm" variant="outline" className="w-full" onClick={() => {
                    setPlage({ from: plage.from, to: plage.from });
                    setCalendarOpen(false);
                  }}>
                    Ce jour uniquement
                  </Button>
                )}
                <p className="px-2 pb-1 text-xs text-muted-foreground">
                  {plage?.from && plage?.to ? "Période sélectionnée" : "Choisissez une date de début et de fin"}
                </p>
              </PopoverContent>
            </Popover>
            {periode === "custom" && plage?.from && plage?.to && (
              <button
                type="button"
                aria-label="Effacer la période personnalisée"
                data-testid="filter-period-clear"
                onClick={() => { setPeriode("all"); setPlage(undefined); setCalendarOpen(false); }}
                className="rounded-md p-1.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            variant={showArchived ? "default" : "outline"}
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            data-testid="button-toggle-archives"
          >
            <Archive className="h-4 w-4 mr-2" />
            {showArchived ? "Vue active" : "Voir les archives"}
          </Button>
          {!showArchived && (
            <Button onClick={openCreate} data-testid="button-new-ticket">
              <Plus className="h-4 w-4 mr-2" /> Nouveau ticket
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <ToggleGroup
          type="single"
          value={filterOrigin}
          onValueChange={(value) => {
            if (value) setFilterOrigin(value as TicketOrigin);
          }}
          aria-label="Origine des tickets"
          data-testid="filter-ticket-origin"
          size="sm"
          className="grid w-full min-w-0 grid-cols-2 rounded-lg border bg-card p-1 sm:flex sm:w-auto"
        >
          {([
            ["all", "Tous"],
            ["client", "Clients"],
            ["revendeur", "Revendeurs"],
            ["unassigned", "À rattacher"],
          ] as const).map(([value, label]) => (
            <ToggleGroupItem
              key={value}
              value={value}
              aria-label={`${label} (${originCounts[value]})`}
              data-testid={`filter-origin-${value}`}
              className={`min-w-0 whitespace-nowrap text-xs sm:text-sm ${
                value === "unassigned"
                  ? "text-muted-foreground data-[state=on]:bg-muted data-[state=on]:text-foreground"
                  : "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              }`}
            >
              {label} <span className="tabular-nums">({originCounts[value]})</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="input-search-tickets"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-[160px] justify-between font-normal"
              data-testid="dropdown-filter-status"
            >
              <span className="truncate text-sm">
                {filterStatus.length === 0
                  ? "Tous les statuts"
                  : filterStatus.length <= 2
                    ? filterStatus.map(s => STATUS_LABELS[s]).join(", ")
                    : `${filterStatus.length} statuts`}
              </span>
              <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[190px]">
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <DropdownMenuCheckboxItem
                key={k}
                checked={filterStatus.includes(k)}
                onCheckedChange={(checked) =>
                  setFilterStatus(prev => checked ? [...prev, k] : prev.filter(s => s !== k))
                }
                onSelect={(e) => e.preventDefault()}
                data-testid={`filter-status-${k}`}
              >
                {v}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => setFilterStatus([])}
              disabled={filterStatus.length === 0}
              className="text-xs text-muted-foreground justify-center"
              data-testid="filter-status-reset"
            >
              Réinitialiser
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[140px]" data-testid="select-filter-priority">
            <SelectValue placeholder="Priorité" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterTechnician} onValueChange={setFilterTechnician}>
          <SelectTrigger className="w-[180px]" data-testid="select-filter-technician">
            <SelectValue placeholder="Technicien" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            {technicians.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.firstName} {t.lastName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterClient} onValueChange={setFilterClient}>
          <SelectTrigger className="w-[180px]" data-testid="select-filter-client">
            <SelectValue placeholder="Client" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            {clients_list.map((c) => (
              <SelectItem key={c.id} value={c.id}>{(c as any).nomCommercial || c.companyName}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Bascule vue Cartes / Liste */}
        <div className="flex items-center rounded-lg border bg-card p-0.5 gap-0.5 ml-auto shrink-0" data-testid="toggle-view-mode">
          <button
            onClick={() => switchView('cards')}
            data-testid="button-view-cards"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'cards' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <LayoutGrid className="h-4 w-4" />
            <span className="hidden sm:inline">Cartes</span>
          </button>
          <button
            onClick={() => switchView('list')}
            data-testid="button-view-list"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">Liste</span>
          </button>
        </div>
      </div>

      {/* ── Vue CARTES ────────────────────────────────────────────────────── */}
      {viewMode === 'cards' && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              {periodEmptyLabel}
            </div>
          ) : (
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
              data-testid="grid-tickets-cards"
            >
              {filtered.map((ticket) => {
                const techName = ticket.assignedTo ? getProfileName(ticket.assignedTo) : null;
                const techProfile = ticket.assignedTo ? profiles_list.find(p => p.id === ticket.assignedTo) : null;
                const robotLabel = getPuduRobotName((ticket as any).puduRobotId ?? null, ticket.robotId ?? null);
                const clientLabel = getClientOrResellerName(ticket);
                const dateLabel = ticket.receivedAt
                  ? format(new Date(ticket.receivedAt), "d MMM", { locale: fr })
                  : null;
                return (
                  <div
                    key={ticket.id}
                    role="button"
                    tabIndex={0}
                    data-testid={`card-ticket-${ticket.id}`}
                    onClick={() => openDetail(ticket)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(ticket); } }}
                    className={`group rounded-xl border bg-card p-4 cursor-pointer transition-all duration-150 hover:border-foreground/30 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${(ticket as any).archive ? "opacity-70" : ""}`}
                  >
                    {/* En-tête : numéro + statut */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono text-xs font-semibold text-muted-foreground" data-testid={`text-card-ticket-number-${ticket.id}`}>
                          {ticket.ticketNumber}
                        </span>
                        {(ticket as any).archive && (
                          <Badge variant="secondary" className="text-[10px] bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 border-0">
                            <Archive className="h-2.5 w-2.5 mr-1" />Archivé
                          </Badge>
                        )}
                        {ticket.source === 'portail_partenaire' && (
                          <Badge variant="secondary" className="text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border-0">
                            Portail
                          </Badge>
                        )}
                      </div>
                      <StatusBadge status={ticket.status} type="ticket" label={STATUS_LABELS[ticket.status] || ticket.status} />
                    </div>

                    {/* Client + priorité */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-sm font-medium text-foreground truncate flex-1" data-testid={`text-card-client-${ticket.id}`}>
                        {clientLabel !== "-" ? clientLabel : (ticket as any).clientFinalNom || <span className="text-muted-foreground italic text-xs">Sans client</span>}
                      </span>
                      <StatusBadge status={ticket.priority} type="ticket_priority" label={PRIORITY_LABELS[ticket.priority] || ticket.priority} />
                    </div>

                    {/* Description tronquée */}
                    <p
                      className="text-xs text-muted-foreground mb-3 leading-relaxed"
                      style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" } as any}
                      data-testid={`text-card-desc-${ticket.id}`}
                    >
                      {(ticket as any).problemDescription?.trim() || <span className="italic">Pas de description</span>}
                    </p>

                    {/* Pied : technicien · robot · date */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground border-t border-border/50 pt-2.5 mt-auto flex-wrap">
                      {techProfile ? (
                        <div className="flex items-center gap-1 min-w-0">
                          <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-[9px] font-bold text-primary">
                            {techProfile.firstName?.[0]}{techProfile.lastName?.[0]}
                          </div>
                          <span className="truncate max-w-[90px]">{techName}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          <span className="italic">Non assigné</span>
                        </div>
                      )}
                      {robotLabel && robotLabel !== "-" && (
                        <>
                          <span className="text-border">·</span>
                          <div className="flex items-center gap-1 min-w-0">
                            <Bot className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate max-w-[90px]">{robotLabel}</span>
                          </div>
                        </>
                      )}
                      {(ticket as any).messageCount > 0 && (
                        <>
                          <span className="text-border">·</span>
                          <div className="flex items-center gap-1">
                            <MessageCircle className="h-3.5 w-3.5 shrink-0" />
                            <span>{(ticket as any).messageCount}</span>
                          </div>
                        </>
                      )}
                      {dateLabel && (
                        <>
                          <span className="text-border ml-auto">·</span>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5 shrink-0" />
                            <span>{dateLabel}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Vue LISTE (tableau inchangé) ──────────────────────────────────── */}
      {viewMode === 'list' && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>{showArchived ? "Reçu le" : "Date"}</TableHead>
                {showArchived && <TableHead>Archivé le</TableHead>}
                <TableHead>Client / Revendeur</TableHead>
                <TableHead>Priorité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Technicien</TableHead>
                <TableHead>Robot</TableHead>
                <TableHead></TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showArchived ? 10 : 9} className="text-center py-8 text-muted-foreground">
                    {periodEmptyLabel}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className={`cursor-pointer hover:bg-muted/50 ${(ticket as any).archive ? "opacity-75" : ""} ${(ticket as any).isMine ? "border-l-[3px] border-l-[hsl(var(--accent-mine))]" : ""}`}
                    onClick={() => openDetail(ticket)}
                    data-testid={`row-ticket-${ticket.id}`}
                  >
                    <TableCell className="font-mono text-sm" data-testid={`text-ticket-number-${ticket.id}`}>
                      <div className="flex items-center gap-2">
                        {ticket.ticketNumber}
                        {(ticket as any).archive && (
                          <Badge variant="secondary" className="text-[10px] bg-gray-200 text-gray-600">
                            <Archive className="h-3 w-3 mr-1" />
                            Archivé
                          </Badge>
                        )}
                        {ticket.source === 'portail_partenaire' && (
                          <Badge variant="secondary" className="text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border-0">
                            Portail
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {ticket.receivedAt
                        ? format(new Date(ticket.receivedAt), "dd/MM/yyyy", { locale: fr })
                        : "-"}
                    </TableCell>
                    {showArchived && (
                      <TableCell className="text-muted-foreground text-xs">
                        {(ticket as any).dateArchivage
                          ? format(new Date((ticket as any).dateArchivage), "dd/MM/yyyy", { locale: fr })
                          : "-"}
                      </TableCell>
                    )}
                    <TableCell>{getClientOrResellerName(ticket)}</TableCell>
                    <TableCell>
                      <StatusBadge
                        status={ticket.priority}
                        type="ticket_priority"
                        label={PRIORITY_LABELS[ticket.priority] || ticket.priority}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={ticket.status}
                        type="ticket"
                        label={STATUS_LABELS[ticket.status] || ticket.status}
                      />
                    </TableCell>
                    <TableCell>{getProfileName(ticket.assignedTo)}</TableCell>
                    <TableCell>{getPuduRobotName((ticket as any).puduRobotId ?? null, ticket.robotId ?? null)}</TableCell>
                    <TableCell>
                      {(ticket as any).deplacementTechnicien && (
                        <Badge variant="outline" className="text-xs border-orange-300 text-orange-600 bg-orange-50" data-testid={`badge-deplacement-${ticket.id}`}>
                          <Truck className="h-3 w-3 mr-1" />
                          Déplacement
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {(ticket as any).archive ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUnarchive(ticket.id)}
                            title="Désarchiver"
                            data-testid={`button-unarchive-ticket-${ticket.id}`}
                          >
                            <ArchiveRestore className="h-4 w-4 text-blue-600" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleArchive(ticket.id)}
                            title="Archiver"
                            data-testid={`button-archive-ticket-${ticket.id}`}
                          >
                            <Archive className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`button-delete-ticket-${ticket.id}`}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer ce ticket ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Le ticket {ticket.ticketNumber} sera définitivement supprimé.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={async () => {
                                    await deleteTicket.mutateAsync(ticket.id);
                                    toast({ title: "Ticket supprimé" });
                                  }}
                                >
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTicket ? "Modifier le ticket" : "Nouveau ticket"}</DialogTitle>
            <DialogDescription>
              {editingTicket ? `Modifier ${editingTicket.ticketNumber}` : "Créer un nouveau ticket SAV"}
            </DialogDescription>
          </DialogHeader>
          <TicketForm
            form={form}
            setForm={setForm}
            onSubmit={handleSubmit}
            isPending={createTicket.isPending || updateTicket.isPending}
            isEdit={!!editingTicket}
            clients={clients_list}
            resellers={resellers_list}
            profiles={profiles_list}
            robots={robots_list}
            puduRobots={puduRobotsData}
            technicians={technicians}
            products={products_list}
            onStagedFilesChange={!editingTicket ? setPendingMediaFiles : undefined}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedTicketId && editingTicket && (
            <TicketDetail
              ticket={editingTicket}
              ticketId={selectedTicketId}
              profiles={profiles_list}
              clients={clients_list}
              resellers={resellers_list}
              robots={robots_list}
              puduRobots={puduRobotsData}
              products={products_list}
              onEdit={() => {
                setDetailOpen(false);
                openEdit(editingTicket);
              }}
              onUpdateTicket={async (updates) => {
                const updated = await updateTicket.mutateAsync({ id: selectedTicketId, ...updates });
                setEditingTicket(updated);
              }}
              onArchiveChanged={(updated) => setEditingTicket(updated)}
              onDeleted={() => setDetailOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveConfirmId} onOpenChange={(open) => !open && setArchiveConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver ce ticket ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le ticket vient d'être clôturé. Voulez-vous l'archiver ? Il disparaîtra de la vue active mais restera consultable dans les archives.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Non, garder visible</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveConfirmId && handleArchive(archiveConfirmId)}>
              Oui, archiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TicketForm({
  form,
  setForm,
  onSubmit,
  isPending,
  isEdit,
  clients,
  resellers,
  profiles: profilesList,
  robots,
  puduRobots,
  technicians,
  products,
  onStagedFilesChange,
}: {
  form: Partial<InsertTicket> & Record<string, any>;
  setForm: (f: Partial<InsertTicket> & Record<string, any>) => void;
  onSubmit: () => void;
  isPending: boolean;
  isEdit: boolean;
  clients: Client[];
  resellers: Reseller[];
  profiles: Profile[];
  robots: Robot[];
  puduRobots: PuduRobotItem[];
  technicians: Profile[];
  products: Product[];
  onStagedFilesChange?: (files: File[]) => void;
}) {
  const activeProfiles = profilesList.filter(p => p.isActive);
  const creeParOptions = activeProfiles.map(p => ({
    value: p.id,
    label: `${p.firstName} ${p.lastName}`,
    sublabel: p.role === "admin" || p.role === "administration" ? "Admin" : p.role === "technicien" ? "Technicien" : p.role === "commercial" ? "Commercial" : p.role || "",
  }));

  const handleClientChange = (v: string | null) => {
    const updates: any = { ...form, clientId: v, resellerId: v ? null : form.resellerId };
    if (v) {
      const client = clients.find(c => c.id === v);
      if (client) {
        updates.contactName = client.contactName || form.contactName || "";
        updates.contactPhone = client.contactPhone || form.contactPhone || "";
        updates.contactEmail = client.contactEmail || form.contactEmail || "";
      }
    }
    setForm(updates);
  };

  const handleResellerChange = (v: string | null) => {
    const updates: any = { ...form, resellerId: v, clientId: v ? null : form.clientId };
    if (v) {
      const reseller = resellers.find(r => r.id === v);
      if (reseller) {
        const name = [reseller.contactFirstName, reseller.contactLastName].filter(Boolean).join(" ");
        updates.contactName = name || form.contactName || "";
        updates.contactPhone = reseller.contactPhone || form.contactPhone || "";
        updates.contactEmail = reseller.contactEmail || form.contactEmail || "";
      }
    }
    setForm(updates);
  };

  const handleCreeParChange = (v: string | null) => {
    if (v) {
      const p = profilesList.find(pr => pr.id === v);
      const creeParType = p?.role === "technicien" ? "technicien" : p?.role === "commercial" ? "commercial" : "admin";
      setForm({ ...form, creeParId: parseProfileId(v), creeParType });
    } else {
      setForm({ ...form, creeParId: null, creeParType: null });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Référence</Label>
        <Input
          value={isEdit ? (form.objet || "") : ""}
          placeholder={isEdit ? "" : "Générée automatiquement à la création"}
          readOnly
          aria-readonly="true"
          data-testid="input-ticket-reference"
        />
      </div>

      <div>
        <Label>Objet</Label>
        <Input
          value={form.titre || ""}
          onChange={(e) => setForm({ ...form, titre: e.target.value })}
          placeholder="Ex : Panne électrovanne CC1"
          data-testid="input-ticket-title"
        />
        {!isEdit && !String(form.titre || "").trim() && (
          <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1" role="status">
            Objet optionnel : sans objet, le mail utilisera uniquement la référence automatique.
          </p>
        )}
      </div>

      <div>
        <Label>Réf. ancien ticket</Label>
        <Input
          value={form.ancienNumero || ""}
          onChange={(e) => setForm({ ...form, ancienNumero: e.target.value })}
          placeholder="Numéro de l'ancien système..."
          data-testid="input-ancien-numero"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Source</Label>
          <Select value={form.source || "formulaire"} onValueChange={(v: any) => setForm({ ...form, source: v })}>
            <SelectTrigger data-testid="select-ticket-source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SOURCE_LABELS).filter(([k]) => k !== 'portail_partenaire').map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Priorité</Label>
          <Select value={form.priority || "moyen"} onValueChange={(v: any) => setForm({ ...form, priority: v })}>
            <SelectTrigger data-testid="select-ticket-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Statut</Label>
          <Select
            value={form.status || "nouveau"}
            onValueChange={(v: any) => {
              const shouldPrefill = (v === "resolu" || v === "ferme") && !form.dateResolution;
              setForm({
                ...form,
                status: v,
                ...(shouldPrefill ? { dateResolution: new Date().toISOString() as any } : {}),
              });
            }}
          >
            <SelectTrigger data-testid="select-ticket-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Date de résolution</Label>
          <Input
            type="date"
            value={form.dateResolution ? new Date(form.dateResolution).toISOString().slice(0, 10) : ""}
            onChange={(e) => setForm({ ...form, dateResolution: e.target.value ? new Date(e.target.value).toISOString() as any : null })}
            data-testid="input-date-resolution"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Client</Label>
          <SearchSelect
            value={form.clientId || null}
            onValueChange={handleClientChange}
            options={clients.map((c) => ({
              value: c.id,
              label: (c as any).nomCommercial || c.companyName,
              sublabel: extractLocation(c.address),
            }))}
            placeholder="Aucun"
            emptyText="Aucun client trouvé"
            data-testid="select-ticket-client"
          />
        </div>
        <div>
          <Label>Revendeur</Label>
          <SearchSelect
            value={form.resellerId || null}
            onValueChange={handleResellerChange}
            options={resellers.map((r) => ({
              value: r.id,
              label: (r as any).nomCommercial || r.companyName,
              sublabel: extractLocation(r.address),
            }))}
            placeholder="Aucun"
            emptyText="Aucun revendeur trouvé"
            data-testid="select-ticket-reseller"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Nom du contact</Label>
          <Input
            value={form.contactName || ""}
            onChange={(e) => setForm({ ...form, contactName: e.target.value })}
            data-testid="input-contact-name"
          />
        </div>
        <div>
          <Label>Téléphone</Label>
          <Input
            value={form.contactPhone || ""}
            onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
            data-testid="input-contact-phone"
          />
        </div>
        <div>
          <Label>Email</Label>
          <Input
            value={form.contactEmail || ""}
            onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
            data-testid="input-contact-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Technicien assigné</Label>
          <SearchSelect
            value={form.assignedTo ? String(form.assignedTo) : null}
            onValueChange={(v) => setForm({ ...form, assignedTo: parseProfileId(v) })}
            options={technicians.map((t) => ({
              value: t.id,
              label: `${t.firstName} ${t.lastName}`,
              sublabel: t.email || undefined,
            }))}
            placeholder="Non assigné"
            emptyText="Aucun technicien trouvé"
            data-testid="select-ticket-assignee"
          />
        </div>
        <div>
          <Label>Robot concerné (parc déployé)</Label>
          <SearchSelect
            value={(form as any).puduRobotId || null}
            onValueChange={(v) => setForm({ ...form, puduRobotId: v || null })}
            options={puduRobots.map((r) => ({
              value: r.id,
              label: r.robotSn,
              sublabel: [r.productName || r.robotType, r.resellerName].filter(Boolean).join(" · ") || undefined,
              imageUrl: r.productImageUrl || undefined,
            }))}
            placeholder="Aucun"
            emptyText="Aucun robot trouvé"
            data-testid="select-ticket-robot"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Créé par</Label>
          <SearchSelect
            value={form.creeParId ? String(form.creeParId) : null}
            onValueChange={handleCreeParChange}
            options={creeParOptions}
            placeholder="Sélectionner..."
            emptyText="Aucun membre trouvé"
            data-testid="select-cree-par"
          />
        </div>
        <div className="flex items-end pb-1">
          <div className="flex items-center gap-3">
            <Switch
              checked={form.deplacementTechnicien || false}
              onCheckedChange={(checked) => setForm({ ...form, deplacementTechnicien: checked })}
              data-testid="switch-deplacement"
            />
            <Label>Déplacement nécessaire</Label>
          </div>
        </div>
      </div>

      <div>
        <Label>Description du problème</Label>
        <Textarea
          value={form.problemDescription || ""}
          onChange={(e) => setForm({ ...form, problemDescription: e.target.value })}
          rows={4}
          data-testid="textarea-problem-description"
        />
      </div>

      <div>
        <Label>Référence HubSpot (optionnel)</Label>
        <Input
          value={form.hubspotId || ""}
          onChange={(e) => setForm({ ...form, hubspotId: e.target.value })}
          placeholder="ID HubSpot"
          data-testid="input-hubspot-id"
        />
      </div>

      {!isEdit && onStagedFilesChange && (
        <div>
          <p className="text-sm font-medium mb-1.5">Pièces jointes (optionnel)</p>
          <TicketMediaDropzone onFilesChange={onStagedFilesChange} />
        </div>
      )}

      <Button onClick={onSubmit} disabled={isPending} className="w-full" data-testid="button-submit-ticket">
        {isPending ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer le ticket"}
      </Button>
    </div>
  );
}

// ── Composant ligne pièce (module-level, jamais défini dans un render) ────────

function PieceRowItem({
  piece,
  product,
  jobottoAdresse,
  resellerAdresse,
  onUpdate,
  onDelete,
}: {
  piece: TicketPiece & { adresseLivraison?: string | null };
  product: Product | undefined;
  jobottoAdresse: string;
  resellerAdresse: string;
  onUpdate: (updates: Record<string, any>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { toast } = useToast();
  const [adresseLivraison, setAdresseLivraison] = useState<string>(piece.adresseLivraison || "");

  const handleDestinataireChange = async (val: string) => {
    let newAdresse = "";
    if (val === "jobotto") newAdresse = jobottoAdresse;
    else if (val === "revendeur") newAdresse = resellerAdresse;
    // client_final → vide, saisie manuelle
    setAdresseLivraison(newAdresse);
    try {
      await onUpdate({ destinataire: val, adresseLivraison: newAdresse || null });
    } catch (e: any) {
      toast({ title: "Erreur mise à jour destinataire", description: e.message, variant: "destructive" });
    }
  };

  const handleAdresseSave = async (val: string) => {
    try {
      await onUpdate({ adresseLivraison: val.trim() || null });
    } catch (e: any) {
      toast({ title: "Erreur enregistrement adresse", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="border rounded-md p-3 space-y-2" data-testid={`piece-${piece.id}`}>
      {/* Ligne 1 : produit + qté + garantie + supprimer */}
      <div className="flex items-center gap-3">
        {product?.imageUrl && (
          <img src={product.imageUrl} alt="" className="w-10 h-10 object-cover rounded shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{product?.nomFr || "Produit inconnu"}</p>
          {product?.sku && <p className="text-xs text-muted-foreground">{product.sku}</p>}
          {piece.note && <p className="text-xs text-muted-foreground mt-0.5">{piece.note}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Input
            type="number"
            min={1}
            defaultValue={piece.quantite}
            onBlur={async (e) => {
              const q = parseInt(e.target.value) || 1;
              await onUpdate({ quantite: q });
            }}
            className="w-16 h-8 text-xs"
            data-testid={`input-piece-qty-${piece.id}`}
          />
          <div className="flex items-center gap-1.5">
            <Switch
              checked={piece.sousGarantie || false}
              onCheckedChange={async (checked) => { await onUpdate({ sousGarantie: checked }); }}
              data-testid={`switch-piece-warranty-${piece.id}`}
            />
            {piece.sousGarantie && (
              <Badge className="bg-green-100 text-green-700 text-xs border-green-200">Garantie</Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive"
            onClick={onDelete}
            data-testid={`button-delete-piece-${piece.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Ligne 2 : N° demande SP · N° de suivi · Réception prévue */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground whitespace-nowrap shrink-0">N° SP</Label>
          <Input
            className="h-7 text-xs flex-1"
            placeholder="SP-XXXXXXXXXXXX"
            defaultValue={(piece as any).puduMaterialRequest || ""}
            onBlur={async (e) => {
              const val = e.target.value.trim() || null;
              await onUpdate({ puduMaterialRequest: val });
            }}
            data-testid={`input-piece-material-${piece.id}`}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground whitespace-nowrap shrink-0">Suivi</Label>
          <Input
            className="h-7 text-xs flex-1"
            placeholder="UPS / FedEx…"
            defaultValue={(piece as any).trackingNumber || ""}
            onBlur={async (e) => {
              const val = e.target.value.trim() || null;
              await onUpdate({ trackingNumber: val });
            }}
            data-testid={`input-piece-tracking-${piece.id}`}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground whitespace-nowrap shrink-0">Réception</Label>
          <Input
            type="date"
            className="h-7 text-xs flex-1"
            defaultValue={(piece as any).dateReceptionPrevue
              ? new Date((piece as any).dateReceptionPrevue).toISOString().slice(0, 10)
              : ""}
            onBlur={async (e) => {
              const val = e.target.value || null;
              await onUpdate({ dateReceptionPrevue: val });
            }}
            data-testid={`input-piece-date-${piece.id}`}
          />
        </div>
      </div>

      {/* Ligne 3 : Destinataire · Adresse livraison */}
      <div className="flex gap-2 flex-wrap sm:flex-nowrap items-start">
        <div className="flex items-center gap-1.5 shrink-0">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">Destinataire</Label>
          <Select
            value={(piece as any).destinataire ?? undefined}
            onValueChange={handleDestinataireChange}
          >
            <SelectTrigger className="h-7 text-xs w-36" data-testid={`select-piece-destinataire-${piece.id}`}>
              <SelectValue placeholder="Destinataire…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="jobotto">Jobotto</SelectItem>
              <SelectItem value="revendeur">Revendeur</SelectItem>
              <SelectItem value="client_final">Client final</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-start gap-1.5 flex-1 min-w-0">
          <Label className="text-xs text-muted-foreground whitespace-nowrap mt-1.5">Adresse</Label>
          <Textarea
            className="text-xs flex-1 min-h-[52px] resize-none"
            placeholder="Adresse de livraison (optionnel)"
            value={adresseLivraison}
            onChange={(e) => setAdresseLivraison(e.target.value)}
            onBlur={() => handleAdresseSave(adresseLivraison)}
            data-testid={`textarea-piece-adresse-${piece.id}`}
          />
        </div>
      </div>
    </div>
  );
}

function MediasTabLabel({ ticketId }: { ticketId: string }) {
  const { data: mediaList = [] } = useTicketMedia(ticketId);
  return <>Pièces jointes ({(mediaList as any[]).length})</>;
}

function TicketDetail({
  ticket,
  ticketId,
  profiles: profilesList,
  clients,
  resellers,
  robots,
  puduRobots,
  products,
  onEdit,
  onUpdateTicket,
  onArchiveChanged,
  onDeleted,
}: {
  ticket: Ticket;
  ticketId: string;
  profiles: Profile[];
  clients: Client[];
  resellers: Reseller[];
  robots: Robot[];
  puduRobots: PuduRobotItem[];
  products: Product[];
  onEdit: () => void;
  onUpdateTicket: (updates: Partial<InsertTicket>) => Promise<void>;
  onArchiveChanged: (updated: Ticket) => void;
  onDeleted: () => void;
}) {
  const { toast } = useToast();
  const { profile } = useAuth();
  const { data: notesData = [] } = useTicketNotes(ticketId);
  const { data: ticketMissionsData = [] } = useTicketMissions(ticketId);
  const { data: missionsData = [] } = useMissions();
  const { data: messagesData = [] } = useTicketMessages(ticketId);
  const { data: piecesData = [] } = useTicketPieces(ticketId);
  const { data: ticketEmailModelsData } = useQuery<{
    modeles: Array<{ code: string; libelle: string }>;
  }>({
    queryKey: [`/api/tickets/${ticketId}/emails/modeles`],
    queryFn: async () => {
      const res = await authFetch(`/api/tickets/${ticketId}/emails/modeles`);
      if (!res.ok) return { modeles: [] };
      return res.json();
    },
    enabled: !!ticketId,
  });
  const { data: emailSettingsData } = useQuery({
    queryKey: ["/api/email-settings"],
    queryFn: async () => {
      const res = await authFetch("/api/email-settings");
      if (!res.ok) return null;
      return res.json();
    },
  });
  const jobottoAdresse: string = emailSettingsData?.footerAdresse || "";
  const ticketReseller = resellers.find(r => r.id === ticket.resellerId);
  const resellerAdresse: string = ticketReseller
    ? (
        [
          (ticketReseller as any).facturationAdresse,
          [(ticketReseller as any).facturationCodePostal, (ticketReseller as any).facturationVille]
            .filter(Boolean).join(" "),
        ].filter(Boolean).join(", ")
      ) || (ticketReseller as any).address || ""
    : "";
  const createNote = useCreateTicketNote();
  const createTicketMission = useCreateTicketMission();
  const createMission = useCreateMission();
  const createMessage = useCreateTicketMessage();
  const updateMessage = useUpdateTicketMessage();
  const canAdmin = canDo(profile?.accessRole);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [editingMentionedIds, setEditingMentionedIds] = useState<string[]>([]);
  const savingEditRef = useRef(false);
  const createPiece = useCreateTicketPiece();
  const updatePiece = useUpdateTicketPiece();
  const deletePiece = useDeleteTicketPiece();
  const canDelete = profile?.accessRole === 'admin' || profile?.accessRole === 'super_admin';
  const archiveTicket = useArchiveTicket();
  const unarchiveTicket = useUnarchiveTicket();
  const deleteTicket = useDeleteTicket();
  const [, setLocation] = useLocation();
  const [noteText, setNoteText] = useState("");
  const [messageText, setMessageText] = useState("");
  const [clientEmailOpenRequest, setClientEmailOpenRequest] = useState<TicketClientEmailOpenRequest | null>(null);
  const [messageMentionedIds, setMessageMentionedIds] = useState<string[]>([]);
  const [selectedProduitId, setSelectedProduitId] = useState<string | null>(null);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState<string | null>(null);
  const [iaCopied, setIaCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [woValue, setWoValue] = useState<string>((ticket as any).puduWorkOrder || "");
  const [woSaving, setWoSaving] = useState(false);
  const woLockRef = useRef(false);

  const handleGenerateResume = async () => {
    setIaLoading(true);
    setIaError(null);
    try {
      const res = await authFetch(`/api/tickets/${ticketId}/resume`, { method: "POST" });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || `Erreur ${res.status}`);
      }
      await queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
    } catch (e: any) {
      setIaError("Impossible de générer le résumé. Réessayez.");
    } finally {
      setIaLoading(false);
    }
  };

  const handleCopyResume = async () => {
    if (!(ticket as any).resumeIa) return;
    await navigator.clipboard.writeText((ticket as any).resumeIa);
    setIaCopied(true);
    setTimeout(() => setIaCopied(false), 2000);
  };

  const notes: TicketNote[] = notesData;
  const missions: Mission[] = missionsData;
  const ticketMissionLinks: TicketMission[] = ticketMissionsData;
  const messages: TicketMessage[] = messagesData;
  const pieces: TicketPiece[] = piecesData;

  const linkedMissions = missions.filter((m) =>
    ticketMissionLinks.some((tm) => tm.missionId === m.id)
  );

  const client = clients.find((c) => c.id === ticket.clientId);
  const reseller = resellers.find((r) => r.id === ticket.resellerId);
  const missionTierName = (mission: Mission): string | null => {
    const tierId = mission.tiersId ||
      (mission.tiersType === "client" ? mission.clientId : null) ||
      (mission.tiersType === "prospect" ? mission.prospectId : null);
    if (!tierId) return null;
    if (mission.tiersType === "client" || (!mission.tiersType && mission.clientId)) {
      return resolveMissionTierName(clients.find((entry) => entry.id === tierId));
    }
    if (mission.tiersType === "revendeur") {
      return resolveMissionTierName(resellers.find((entry) => entry.id === tierId));
    }
    return null;
  };
  const availableMissionOptions = useMemo(() => {
    const linkedIds = new Set(ticketMissionLinks.map((link) => link.missionId));
    return missions
      .filter((mission) => !linkedIds.has(mission.id))
      .map((mission) => {
        const tierName = missionTierName(mission);
        const title = resolveMissionTitle(mission, tierName);
        const typeLabel = MISSION_TYPES[mission.missionType]?.label || mission.missionType;
        const statusLabel = MISSION_STATUS_LABELS[mission.status] || mission.status;
        const dateLabel = mission.startDate
          ? format(new Date(mission.startDate), "dd/MM/yyyy", { locale: fr })
          : "Date non renseignée";
        const secondaryParts = [
          dateLabel,
          typeLabel,
          statusLabel,
          tierName && tierName.trim().toLocaleLowerCase("fr-FR") !== title.trim().toLocaleLowerCase("fr-FR")
            ? tierName
            : null,
        ].filter((part): part is string => Boolean(part));
        const missionClientId = mission.tiersType === "client"
          ? mission.tiersId
          : mission.clientId;
        return {
          value: mission.id,
          label: title,
          sublabel: secondaryParts.join(" · "),
          belongsToTicketClient: Boolean(ticket.clientId && missionClientId === ticket.clientId),
          timestamp: mission.startDate ? new Date(mission.startDate).getTime() : 0,
        };
      })
      .sort((a, b) =>
        Number(b.belongsToTicketClient) - Number(a.belongsToTicketClient) ||
        b.timestamp - a.timestamp ||
        a.label.localeCompare(b.label, "fr")
      )
      .map(({ belongsToTicketClient: _belongs, timestamp: _timestamp, ...option }) => option);
  }, [missions, ticketMissionLinks, ticket.clientId, clients, resellers]);
  const puduRobot = puduRobots.find((r) => r.id === (ticket as any).puduRobotId);
  const robot = !puduRobot ? robots.find((r) => r.id === ticket.robotId) : undefined;
  const assignee = profilesList.find((p) => p.id === ticket.assignedTo);
  const creePar = profilesList.find((p) => p.id === (ticket as any).creeParId);

  const getProfileName = (id: string | null) => {
    if (!id) return "-";
    const p = profilesList.find((p) => p.id === id);
    return p ? `${p.firstName} ${p.lastName}` : "-";
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await createNote.mutateAsync({ ticketId, content: noteText.trim() });
      setNoteText("");
      toast({ title: "Note ajoutée" });
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || savingEditRef.current) return;
    const trimmed = editingContent.trim();
    if (!trimmed) return;
    savingEditRef.current = true;
    try {
      const validMentions = editingMentionedIds.filter(id => {
        const p = profilesList.find(x => x.id === id);
        return p ? trimmed.includes(`@${p.firstName} ${p.lastName}`) : false;
      });
      await updateMessage.mutateAsync({ ticketId, msgId: editingMessageId, contenu: trimmed, mentionedUserIds: validMentions.length ? validMentions : undefined });
      setEditingMessageId(null);
      setEditingContent("");
      setEditingMentionedIds([]);
      toast({ title: "Message modifié" });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      savingEditRef.current = false;
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    try {
      const auteur = profile ? `${profile.firstName} ${profile.lastName}` : "Système";
      const text = messageText.trim();
      const validMentions = messageMentionedIds.filter(id => {
        const p = profilesList.find(x => x.id === id);
        return p ? text.includes(`@${p.firstName} ${p.lastName}`) : false;
      });
      await createMessage.mutateAsync({ ticketId, auteur, auteurId: profile?.id, contenu: text, mentionedUserIds: validMentions.length ? validMentions : undefined });
      setMessageText("");
      setMessageMentionedIds([]);
      toast({ title: "Message envoyé" });
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  const handleCreateMissionFromTicket = async () => {
    try {
      const missionData: any = {
        missionType: "installation",
        status: "option",
        startDate: new Date().toISOString().slice(0, 10),
        endDate: new Date().toISOString().slice(0, 10),
        createdBy: profile?.id || "",
        description: `Mission créée depuis le ticket ${ticket.ticketNumber}${ticket.problemDescription ? `\n\n${ticket.problemDescription}` : ""}`,
      };
      if (ticket.clientId) missionData.clientId = ticket.clientId;
      if (ticket.robotId) missionData.robotIds = [ticket.robotId];

      const newMission = await createMission.mutateAsync(missionData);
      await createTicketMission.mutateAsync({ ticketId, missionId: newMission.id });
      toast({ title: "Mission créée et associée au ticket" });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    }
  };

  const handleLinkMission = async (missionId: string) => {
    try {
      await createTicketMission.mutateAsync({ ticketId, missionId });
      toast({ title: "Mission associée" });
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  const handleAddPiece = async () => {
    if (!selectedProduitId) return;
    try {
      await createPiece.mutateAsync({ ticketId, produitId: selectedProduitId });
      setSelectedProduitId(null);
      toast({ title: "Pièce ajoutée" });
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3 flex-wrap">
          <span>{ticket.titre?.trim() || "(sans objet)"}</span>
          <span className="font-mono text-xs text-muted-foreground font-normal">
            {ticket.objet || ticket.ticketNumber}
          </span>
          {(ticket as any).ancienNumero && (
            <span className="text-xs text-muted-foreground font-normal">(Réf: {(ticket as any).ancienNumero})</span>
          )}
          <StatusBadge status={ticket.status} type="ticket" label={STATUS_LABELS[ticket.status]} />
          <StatusBadge status={ticket.priority} type="ticket_priority" label={PRIORITY_LABELS[ticket.priority]} />
          {(ticket as any).deplacementTechnicien && (
            <Badge variant="outline" className="text-xs border-orange-300 text-orange-600 bg-orange-50">
              <Truck className="h-3 w-3 mr-1" />
              Déplacement
            </Badge>
          )}
          {(ticket as any).archive && (
            <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-600">
              <Archive className="h-3 w-3 mr-1" />
              Archivé
            </Badge>
          )}
          {(ticket as any).prestationId && (
            <Badge variant="outline" className="text-xs border-violet-300 text-violet-700 bg-violet-50 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-700">
              <Briefcase className="h-3 w-3 mr-1" />
              Sous-traitance en cours
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 px-2 text-xs text-muted-foreground hover:text-foreground gap-1.5 shrink-0"
            onClick={() => {
              const url = `${window.location.origin}/tickets?open=${ticket.ticketNumber}`;
              navigator.clipboard.writeText(url);
              setLinkCopied(true);
              setTimeout(() => setLinkCopied(false), 1800);
            }}
            data-testid="button-copy-link"
          >
            {linkCopied ? (
              <><Copy className="h-3.5 w-3.5" />Copié !</>
            ) : (
              <><Link2 className="h-3.5 w-3.5" />Copier le lien</>
            )}
          </Button>
        </DialogTitle>
        <DialogDescription>
          Créé le {ticket.receivedAt ? format(new Date(ticket.receivedAt), "dd/MM/yyyy à HH:mm", { locale: fr }) : "-"}
          {(ticket as any).dateResolution && (
            <span className="ml-2 text-green-600">· Résolu le {format(new Date((ticket as any).dateResolution), "dd/MM/yyyy", { locale: fr })}</span>
          )}
        </DialogDescription>
      </DialogHeader>

      {/* ── Bloc Résumé IA ── */}
      {(notes.length + messages.length >= 3 || (ticket as any).resumeIa) && (
        <div className="mt-4 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/30 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-violet-700 dark:text-violet-400 font-semibold text-sm">
              <Sparkles className="w-4 h-4" />
              Résumé généré par IA
              {(ticket as any).resumeIaDate && (
                <span className="text-xs font-normal text-muted-foreground">
                  — Généré le {format(new Date((ticket as any).resumeIaDate), "dd/MM/yyyy à HH:mm", { locale: fr })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(ticket as any).resumeIa && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCopyResume} data-testid="button-copy-resume">
                  <Copy className="w-3.5 h-3.5 mr-1" />
                  {iaCopied ? "Copié !" : "Copier"}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-xs border-violet-300 dark:border-violet-700"
                onClick={handleGenerateResume}
                disabled={iaLoading}
                data-testid="button-generate-resume"
              >
                {iaLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Analyse en cours...</>
                ) : (ticket as any).resumeIa ? (
                  <><RefreshCw className="w-3.5 h-3.5 mr-1" />Régénérer</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5 mr-1" />Générer un résumé</>
                )}
              </Button>
            </div>
          </div>
          {iaError && <p className="text-xs text-destructive">{iaError}</p>}
          {(ticket as any).resumeIa && !iaLoading && (
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{(ticket as any).resumeIa}</p>
          )}
        </div>
      )}

      <Tabs defaultValue="info" className="mt-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="info" data-testid="tab-info">Infos</TabsTrigger>
          <TabsTrigger value="discussion" data-testid="tab-discussion">
            <MessageSquare className="h-3.5 w-3.5 mr-1" />
            Discussion ({messages.length})
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="missions" data-testid="tab-missions">Missions ({linkedMissions.length})</TabsTrigger>
          <TabsTrigger value="pieces" data-testid="tab-pieces">Pièces ({pieces.length})</TabsTrigger>
          <TabsTrigger value="medias" data-testid="tab-medias"><MediasTabLabel ticketId={ticketId} /></TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Client</span>
              <p className="font-medium">{(client as any)?.nomCommercial || client?.companyName || "-"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Revendeur</span>
              <p className="font-medium">{(reseller as any)?.nomCommercial || reseller?.companyName || "-"}</p>
            </div>
            {(ticket as any).clientFinalNom && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Client final</span>
                <p className="font-medium">{(ticket as any).clientFinalNom}</p>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Contact</span>
              <p className="font-medium">{ticket.contactName || "-"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Téléphone</span>
              <p className="font-medium">{ticket.contactPhone || "-"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Email</span>
              <p className="font-medium">{ticket.contactEmail || "-"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Source</span>
              <p className="font-medium">{SOURCE_LABELS[ticket.source] || ticket.source}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Technicien assigné</span>
              <p className="font-medium">{assignee ? `${assignee.firstName} ${assignee.lastName}` : "Non assigné"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Robot concerné</span>
              <p className="font-medium">
                {puduRobot ? (
                  <span className="flex items-center gap-1.5" data-testid="robot-pudu-display">
                    {puduRobot.productImageUrl && (
                      <img src={puduRobot.productImageUrl} alt="" className="h-5 w-5 rounded object-contain shrink-0" />
                    )}
                    <span className="font-semibold">{puduRobot.robotSn}</span>
                    {(puduRobot.productName || puduRobot.resellerName) && (
                      <span className="text-xs text-muted-foreground">
                        — {[puduRobot.productName, puduRobot.resellerName].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                ) : robot ? (
                  <button
                    className="text-primary cursor-pointer hover:underline text-left"
                    onClick={() => setLocation("/robots")}
                    data-testid="link-robot"
                  >
                    <ExternalLink className="inline h-3 w-3 mr-1" />
                    {robot.modelName}{robot.nickname ? ` (${robot.nickname})` : ""}
                  </button>
                ) : "-"}
              </p>
            </div>
            {creePar && (
              <div>
                <span className="text-muted-foreground">Créé par</span>
                <p className="font-medium">{creePar.firstName} {creePar.lastName} <span className="text-xs text-muted-foreground">({(ticket as any).creeParType})</span></p>
              </div>
            )}
            {(ticket as any).dateResolution && (
              <div>
                <span className="text-muted-foreground">Date de résolution</span>
                <p className="font-medium text-green-600">{format(new Date((ticket as any).dateResolution), "dd/MM/yyyy", { locale: fr })}</p>
              </div>
            )}
          </div>
          {ticket.problemDescription && (
            <div>
              <span className="text-sm text-muted-foreground">Description du problème</span>
              <p className="mt-1 text-sm bg-muted/50 p-3 rounded-md whitespace-pre-wrap" onCopy={handlePlainTextCopy}>
                {ticket.problemDescription}
              </p>
            </div>
          )}
          {ticket.hubspotId && (
            <div>
              <span className="text-sm text-muted-foreground">Référence HubSpot</span>
              <p className="font-mono text-sm">{ticket.hubspotId}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button onClick={onEdit} variant="outline" data-testid="button-edit-ticket">
              Modifier
            </Button>
            {(ticket as any).archive ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  const updated = await unarchiveTicket.mutateAsync(ticketId);
                  toast({ title: "Ticket désarchivé" });
                  onArchiveChanged(updated);
                }}
                title="Désarchiver"
                data-testid="button-unarchive-ticket"
              >
                <ArchiveRestore className="h-4 w-4 text-blue-600" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  const updated = await archiveTicket.mutateAsync(ticketId);
                  toast({ title: "Ticket archivé" });
                  onArchiveChanged(updated);
                }}
                title="Archiver"
                data-testid="button-archive-ticket"
              >
                <Archive className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
            {canDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="button-delete-ticket">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer ce ticket ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Le ticket {ticket.ticketNumber} sera définitivement supprimé.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        await deleteTicket.mutateAsync(ticketId);
                        toast({ title: "Ticket supprimé" });
                        onDeleted();
                      }}
                    >
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </TabsContent>

        <TabsContent value="discussion" className="space-y-4 mt-4">
          <div className="flex items-center justify-between gap-3 border-b pb-3">
            <div>
              <h3 className="text-sm font-semibold">Fil de discussion</h3>
              <p className="text-xs text-muted-foreground">Messages internes et échanges email avec le client</p>
            </div>
            <TicketClientEmail ticketId={ticketId} openRequest={clientEmailOpenRequest} />
          </div>
          <div className="space-y-3 max-h-[400px] overflow-y-auto" onCopy={handlePlainTextCopy}>
            {messages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucun message</p>
            ) : (
              messages.map((msg) => {
                const emailModeleCode = (msg as any).modeleCode as string | null;
                const emailModeleLibelle = resolveEmailModelLabel(
                  emailModeleCode,
                  ticketEmailModelsData?.modeles,
                );
                const initials = (msg.auteur || "Email").split(" ").map(w => w[0]?.toUpperCase()).join("").slice(0, 2);
                const isEditing = editingMessageId === msg.id;
                if (!isEditing) {
                  return (
                    <TicketDiscussionEntry
                      key={msg.id}
                      message={msg as any}
                      emailModelLabel={emailModeleLibelle}
                      profiles={profilesList}
                      canAdmin={canAdmin}
                      currentProfileId={profile?.id}
                      onEdit={(message) => {
                        setEditingMessageId(message.id);
                        setEditingContent(message.contenu);
                        setEditingMentionedIds(message.mentionedUserIds || []);
                      }}
                      onReplyToClient={() => {
                        setClientEmailOpenRequest({
                          id: Date.now(),
                          modeleCode: "reponse_libre",
                        });
                      }}
                    />
                  );
                }
                return (
                  <div key={msg.id} className="flex gap-3 group rounded-md p-2" data-testid={`message-${msg.id}`}>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold bg-primary/10 text-primary">
                      {initials}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{msg.auteur}</span>
                        <span>{msg.createdAt ? format(new Date(msg.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }) : ""}</span>
                        {(msg as any).editedAt && <span className="italic text-muted-foreground/60">(modifié)</span>}
                      </div>
                      <div className="mt-1.5 space-y-2">
                        <MentionTextarea
                          value={editingContent}
                          onChange={setEditingContent}
                          onMentionedIdsChange={setEditingMentionedIds}
                          profiles={profilesList}
                          rows={2}
                          className="w-full"
                          data-testid="textarea-edit-message"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={!editingContent.trim() || updateMessage.isPending}
                            data-testid="button-save-edit-message"
                          >
                            {updateMessage.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                            Enregistrer
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingMessageId(null); setEditingContent(""); setEditingMentionedIds([]); }}
                            data-testid="button-cancel-edit-message"
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex gap-2 border-t pt-3">
            <MentionTextarea
              value={messageText}
              onChange={setMessageText}
              onMentionedIdsChange={setMessageMentionedIds}
              profiles={profilesList}
              placeholder="Message interne à l'équipe — non envoyé au client"
              rows={2}
              className="flex-1"
              data-testid="textarea-new-message"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!messageText.trim() || createMessage.isPending}
              size="icon"
              className="self-end"
              data-testid="button-send-message"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="notes" className="space-y-4 mt-4">
          <div className="space-y-3 max-h-[300px] overflow-y-auto" onCopy={handlePlainTextCopy}>
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune note</p>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="border rounded-md p-3 space-y-1" data-testid={`note-${note.id}`}>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-medium">{getProfileName(note.authorId)}</span>
                    <span>{note.createdAt ? format(new Date(note.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }) : ""}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Ajouter une note..."
              rows={2}
              className="flex-1"
              data-testid="textarea-new-note"
            />
            <Button
              onClick={handleAddNote}
              disabled={!noteText.trim() || createNote.isPending}
              size="icon"
              className="self-end"
              data-testid="button-add-note"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="missions" className="space-y-4 mt-4">
          {linkedMissions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucune mission associée</p>
          ) : (
            <div className="space-y-2">
              {linkedMissions.map((m) => (
                <div key={m.id} className="flex items-center justify-between border rounded-md p-3" data-testid={`linked-mission-${m.id}`}>
                  <div>
                    <span className="font-medium text-sm">{m.missionType}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {m.startDate ? format(new Date(m.startDate), "dd/MM/yyyy", { locale: fr }) : ""}
                    </span>
                  </div>
                  <StatusBadge status={m.status} type="mission" />
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <SearchSelect
              value={null}
              onValueChange={(v) => { if (v) handleLinkMission(v); }}
              options={availableMissionOptions}
              placeholder="Associer une mission existante..."
              emptyText="Aucune mission disponible"
              className="flex-1"
              data-testid="select-link-mission"
            />
          </div>
          <Button
            onClick={handleCreateMissionFromTicket}
            variant="outline"
            disabled={createMission.isPending}
            data-testid="button-create-mission-from-ticket"
          >
            <Plus className="h-4 w-4 mr-2" />
            {createMission.isPending ? "Création..." : "Créer une mission depuis ce ticket"}
          </Button>
        </TabsContent>

        <TabsContent value="pieces" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={ticket.puduWarrantyActive || false}
                onCheckedChange={async (checked) => {
                  await onUpdateTicket({ puduWarrantyActive: checked });
                  toast({ title: checked ? "Garantie Pudu activée" : "Garantie Pudu désactivée" });
                }}
                data-testid="switch-warranty"
              />
              <Label>Garantie Pudu globale</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">Work Order Pudu</Label>
              <Input
                className="h-8 text-xs w-52"
                placeholder="DBWO-XXXXXXXXXXXX"
                value={woValue}
                onChange={(e) => setWoValue(e.target.value)}
                data-testid="input-pudu-work-order"
              />
              <Button
                size="sm"
                className="h-8 text-xs"
                disabled={woSaving}
                onClick={async () => {
                  if (woLockRef.current) return;
                  woLockRef.current = true;
                  setWoSaving(true);
                  try {
                    const val = woValue.trim() || null;
                    await onUpdateTicket({
                      puduWorkOrder: val,
                      puduWarrantyActive: ticket.puduWarrantyActive ?? false,
                    } as any);
                    toast({ title: "Work Order enregistré" });
                  } catch (e: any) {
                    toast({ title: "Erreur enregistrement Work Order", description: e.message, variant: "destructive" });
                  } finally {
                    woLockRef.current = false;
                    setWoSaving(false);
                  }
                }}
                data-testid="button-save-work-order"
              >
                {woSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {pieces.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune pièce</p>
            ) : (
              pieces.map((piece) => (
                <PieceRowItem
                  key={piece.id}
                  piece={piece as any}
                  product={products.find(p => p.id === piece.produitId)}
                  jobottoAdresse={jobottoAdresse}
                  resellerAdresse={resellerAdresse}
                  onUpdate={async (updates) => {
                    await updatePiece.mutateAsync({ id: piece.id, ...updates } as any);
                  }}
                  onDelete={async () => {
                    await deletePiece.mutateAsync(piece.id);
                    toast({ title: "Pièce supprimée" });
                  }}
                />
              ))
            )}
          </div>

          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Ajouter une pièce</Label>
              <SearchSelect
                value={selectedProduitId}
                onValueChange={(v) => setSelectedProduitId(v)}
                options={products.filter(p => p.actif !== false).map(p => ({
                  value: p.id,
                  label: p.nomFr,
                  sublabel: p.sku || undefined,
                  imageUrl: p.imageUrl || undefined,
                }))}
                placeholder="Rechercher un produit..."
                emptyText="Aucun produit trouvé"
                data-testid="select-piece-product"
              />
            </div>
            <Button
              onClick={handleAddPiece}
              disabled={!selectedProduitId || createPiece.isPending}
              size="sm"
              data-testid="button-add-piece"
            >
              <Plus className="h-4 w-4 mr-1" /> Ajouter
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="medias" className="mt-4">
          <TicketMediaTab
            ticketId={ticketId}
            canManage={true}
          />
        </TabsContent>
      </Tabs>
    </>
  );
}
